import * as Sentry from "@sentry/react";
import moment from "moment-timezone";
import { useEffect, useRef, useState } from "react";
import axios from "../../config/axios";
import { buttonIds } from "../../config/buttonIds";
import live_display_socket from "../../config/live_display_socket";
import { useConfig } from "../../hooks/useConfig";
import { useTV } from "../../hooks/useTV";
import useGlobalStore from "../../store/global.store";
import RTVSelector from "../RTVSelector";
import ParkBottomBar from "./ParkBottomBar";
import VipBottomBar from "./VIP/VipBottomBar";
import { useRemote } from "../../hooks/useRemote";

const BottomBar = ({ device }) => {
  const currentView = useGlobalStore((state) => state.currentView);
  const showBottomBar = useGlobalStore((state) => state.showBottomBar);
  const showSettings = useGlobalStore((state) => state.showSettings);
  const hasVipAddOn = useGlobalStore((state) => state.hasVipAddOn);
  const setShowSettings = useGlobalStore((state) => state.setShowSettings);
  const showVipSelector = useGlobalStore((state) => state.showVipSelector);
  const selectedVipDoor = useGlobalStore((state) => state.selectedVipDoor);
  const beaconShowPlaying = useGlobalStore((state) => state.beaconShowPlaying);
  const hasBeaconAddOn = useGlobalStore((state) => state.hasBeaconAddOn);
  const setShowVipSelector = useGlobalStore(
    (state) => state.setShowVipSelector,
  );

  const { updateView } = useRemote();

  const [showResortSelector, setShowResortSelector] = useState(false);
  const [showTimes, setShowTimes] = useState([]);
  const [focusedButton, setFocusedButton] = useState("home");
  const [showParkBar, setShowParkBar] = useState(false);

  const settingsButtonRef = useRef(null);
  const homeButtonRef = useRef(null);
  const dashButtonRef = useRef(null);
  const resortButtonRef = useRef(null);
  const terraceButtonRef = useRef(null);
  const parksButtonRef = useRef(null);
  const vacationButtonRef = useRef(null);
  const photosButtonRef = useRef(null);
  const bandButtonRef = useRef(null);
  const vipButtonRef = useRef(null);

  const { config } = useConfig();
  const { isTv } = useTV();

  // Helper function to determine tabIndex for buttons
  const getTabIndex = () => {
    return showResortSelector || showSettings || showVipSelector ? -1 : 0;
  };

   // ── Release flags: config/Buttons { Name: bool, Name_Override: [subIds],
  //    Name_Vip: bool } ──
  // Absent node or flag = HIDDEN (safe dark default for prod). Overrides
  // show a button to specific subscriptions while its flag is off.
  // Name_Vip: true shows a button to all VIP add-on holders while flag is off.
  const buttonEnabled = (name) => {
    const b = config?.Buttons;
    if (!b) return false;
    if (b[name] === true) return true;
    if (b[`${name}_Vip`] === true && hasVipAddOn) return true;
    const ov = b[`${name}_Override`];
    const id = device?.subscription?._id;
    if (!id) return false;
    if (Array.isArray(ov)) return ov.includes(id);
    if (typeof ov === "string") return ov === id;
    return false;
  };

  const showVipButton = () => {
    if (buttonEnabled("Vip")) return true;
    // Legacy fallback — existing prod behavior preserved.
    if (config.Show_VIP_Button) {
      return true;
    }
    if (
      config.VIP_Button_Override_Activation_List?.includes(
        device.subscription._id,
      )
    ) {
      return true;
    }
    return false;
  };

  // ── Adaptive bar sizing ──────────────────────────────────────────────────
  // The bar always has 4 core text buttons (RESORT/TERRACE/PARKS/VACA + HOME
  // & gear icons); DASH/MEMORIES/BAND+/VIP add up to 4 more. Sizing tiers are
  // anchored at both ends by APPROVED looks:
  //   n=4/5 (production today: VIP only)  -> big & spread (space-around, 55/40)
  //   n=8   (everything on)               -> compact centered (45/31, gap 16)
  // Mid tiers interpolate so every add/remove keeps the bar looking right.
  const nText =
    4 +
    ["Dash", "Memories", "Band"].filter(buttonEnabled).length +
    (showVipButton() ? 1 : 0);
  const TIERS_TV = {
    4: { font: 55, pad: "0px 20px", vipPad: "0px 20px", mode: "space-around", gap: 0, iconW: 70 },
    5: { font: 55, pad: "0px 20px", vipPad: "0px 20px", mode: "space-around", gap: 0, iconW: 70 },
    6: { font: 51, pad: "2px 17px", vipPad: "2px 21px", mode: "center", gap: 30, iconW: 70 },
    7: { font: 48, pad: "3px 15px", vipPad: "3px 22px", mode: "center", gap: 22, iconW: 70 },
    8: { font: 45, pad: "4px 13px", vipPad: "4px 23px", mode: "center", gap: 16, iconW: 70 },
  };
  const TIERS_DISP = {
    4: { font: 40, pad: "0px 20px", vipPad: "0px 20px", mode: "space-around", gap: 0, iconW: 60 },
    5: { font: 40, pad: "0px 20px", vipPad: "0px 20px", mode: "space-around", gap: 0, iconW: 60 },
    6: { font: 37, pad: "2px 15px", vipPad: "2px 19px", mode: "center", gap: 16, iconW: 60 },
    7: { font: 34, pad: "4px 13px", vipPad: "4px 18px", mode: "center", gap: 13, iconW: 58 },
    8: { font: 31, pad: "6px 12px", vipPad: "6px 18px", mode: "center", gap: 10, iconW: 58 },
  };
  const tier = (isTv ? TIERS_TV : TIERS_DISP)[Math.min(Math.max(nText, 4), 8)];

  const buttonStyle = isTv
    ? {
        flexShrink: 0,
        border: "6px solid white",
        background: "transparent",
        color: "white",
        fontFamily: "Lato",
        fontSize: `${tier.font}px`,
        padding: tier.pad,
        borderRadius: "20px",
        fontWeight: "700",
        userSelect: "none",
      }
    : {
        flexShrink: 0,
        border: "2px solid white",
        background: "transparent",
        color: "white",
        fontFamily: "Lato",
        fontSize: `${tier.font}px`,
        padding: tier.pad,
        borderRadius: "16px",
        fontWeight: "700",
        userSelect: "none",
      };

  console.log("Current screen view: ", device.screen_view);
  // Determine whether to show the main bottom bar or the park-specific one
  const isParkView =
    device.screen_view.includes("Ride") || device.screen_view === "AutoScroll";

  const shouldShowParkBar = isTv && isParkView && showParkBar;

  // Check if it's a park view and set the appropriate bar
  useEffect(() => {
    if (isParkView) {
      setShowParkBar(true);
    } else {
      setShowParkBar(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.screen_view, isTv]);

  // Focus button based on active screen for TV
  useEffect(() => {
    if (!showBottomBar) return;
    if (isTv && !showParkBar) {
      // Never steal focus from a button the user has already navigated to.
      const a = document.activeElement;
      if (a && a !== document.body && a.tagName === "BUTTON") return;
      // Determine which button to focus based on device.screen_view
      if (device.screen_view === "Home" && homeButtonRef.current) {
        homeButtonRef.current.focus();
        setFocusedButton("home");
      } else if (
        device.screen_view.includes("RTV") &&
        resortButtonRef.current
      ) {
        resortButtonRef.current.focus();
        setFocusedButton("resort");
      } else if (device.screen_view === "TRC" && terraceButtonRef.current) {
        terraceButtonRef.current.focus();
        setFocusedButton("terrace");
      } else if (
        device.screen_view.includes("Ride") &&
        parksButtonRef.current
      ) {
        parksButtonRef.current.focus();
        setFocusedButton("parks");
      } else if (
        device.screen_view.includes("VC") &&
        vacationButtonRef.current
      ) {
        vacationButtonRef.current.focus();
        setFocusedButton("vacation");
      } else if (
        device.screen_view === "Dashboard" &&
        dashButtonRef.current
      ) {
        dashButtonRef.current.focus();
        setFocusedButton("dash");
      } else if (
        device.screen_view === "Photos" &&
        photosButtonRef.current
      ) {
        photosButtonRef.current.focus();
        setFocusedButton("photos");
      } else if (
        device.screen_view.includes("BAND") &&
        bandButtonRef.current
      ) {
        bandButtonRef.current.focus();
        setFocusedButton("band");
      }
    }
  }, [isTv, device.screen_view, showParkBar, showBottomBar]);


  const updateDeviceView = (view) => {
    axios
      .patch(
        `/users/device/${device._id}`,
        { screen_view: view },
        { withCredentials: true },
      )
      .then(() => {
        live_display_socket.emit("remote_refresh", {
          device_id: device?._id,
        });
      })
      .catch((error) => {});
  };

  const switchToParks = () => {
    if (device.screen_view.includes("Ride") && isTv) {
      setShowParkBar(true);
      return;
    }

    axios
      .get(`/parks/public/${device._id}`, { withCredentials: true })
      .then((response) => {
        if (response.data.parks.length > 0) {
          updateDeviceView("Ride__" + response.data.parks[0].park_code + "_1");
          if (isTv) {
            setShowParkBar(true);
          }
        }
      })
      .catch((error) => {});
  };

  const resortAudioRef = useRef();

  // resort music controller
  useEffect(() => {
    try {
      if (device.screen_view.includes("RTV")) {
        resortAudioRef.current.volume = localStorage.getItem("volume");
        resortAudioRef.current.play();
      } else {
        resortAudioRef.current.pause();
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }, [device, resortAudioRef]);

  // get show time
  const getShowTime = () => {
    axios
      .get("/shows", { withCredentials: true })
      .then((response) => {
        setShowTimes(response.data.shows);
      })
      .catch((error) => {
        // console.log('error', error);
      });
  };

  const backgroundAudioRef = useRef();
  const announcementAudioRef = useRef();

  // stream announcement and sound controller
  useEffect(() => {
    // Move the function inside the useEffect
    const shouldPlayVipBackgroundMusic = () => {
      if (beaconShowPlaying) {
        return false;
      }

      if (!device.trc_music) {
        return false;
      }

      if (device.screen_view === "TRC") {
        return true;
      }

      if (device.screen_view === "VIP") {
        if (selectedVipDoor === "door2") {
          return true;
        } else {
          return false;
        }
      }

      if (device.screen_view === "VIP__piano") {
        return false;
      }

      if (device.screen_view === "VIP__multiview") {
        return false;
      }
      if (device.screen_view === "VIP__upgrade") {
        return false;
      }

      if (device.screen_view.includes("VIP")) {
        return true;
      }

      return false;
    };

    announcementAudioRef.current.volume = Math.min(
      (+localStorage.getItem("volume") || 0.3) + 0.2,
      1,
    );

    if (shouldPlayVipBackgroundMusic()) {
      // console.log('play background sound - trc');
      backgroundAudioRef.current.volume = localStorage.getItem("volume") || 0.3;
      backgroundAudioRef.current.play();
    } else {
      // console.log('pause background sound - trc');
      backgroundAudioRef.current.pause();
    }

    // Check every minute
    const intervalId = setInterval(() => {
      const currentTime = moment().tz("America/New_York"); // EST timezone

      if (
        currentTime.format("HH:mm") === config.terraceOpenTime &&
        device.announcement &&
        device.screen_view !== "VIP__piano" // Do not play announcement if user is on VIP piano stream screen
      ) {
        // console.log('play announcement');
        // announcement disabled (terrace still opens on time)
      }

      // if current minute is 0, get show time
      if (currentTime.format("mm") === "00") {
        getShowTime();
      }
    }, 50000); // 50000ms = 50 seconds

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [
    device,
    backgroundAudioRef,
    announcementAudioRef,
    showTimes,
    config,
    selectedVipDoor,
    hasVipAddOn,
    updateView,
    beaconShowPlaying,
  ]);

  // Helper function to get button style
  const getButtonStyles = (buttonId, baseStyle, isActive) => {
    // Only apply focus styles for TV mode
    if (isTv && focusedButton === buttonId) {
      return {
        ...baseStyle,
        color: isActive ? "#3ADF3C" : "white",
        borderColor: "#3ADF3C",
        outline: "none",
        border: isActive ? "6px solid #3ADF3C" : "6px solid #3ADF3C",
      };
    }

    return {
      ...baseStyle,
      color: isActive ? "#3ADF3C" : "white",
      borderColor: isActive ? "#3ADF3C" : "white",
    };
  };

  const handleVipClick = () => {
    if (hasVipAddOn) {
      updateView("VIP");
    } else {
      updateView("VIP__upgrade");
    }
  };

  // Handler for when Back button is clicked in ParkBottomBar
  const handleParkBarBackClick = () => {
    setShowParkBar(false);
    if (parksButtonRef.current) {
      parksButtonRef.current.focus();
      setFocusedButton("parks");
    }
  };

  const handleVipBackClick = () => {
    console.log("handleVipBackClick called");
    setShowVipSelector(false);
    // Use setTimeout to ensure state is updated before focusing
    setTimeout(() => {
      // Don't steal focus if the user has already navigated to a button.
      const a = document.activeElement;
      if (a && a !== document.body && a.tagName === "BUTTON") return;
      if (vipButtonRef.current) {
        // homeButtonRef.current.focus();
        document.getElementById(buttonIds.homeButton).focus();

        setFocusedButton("home");
      }
    }, 50);
  };

  return (
    <>
      {/* CHANGE START: Glow effect CSS */}
      <style>
        {`
    .vip-button {
      position: relative;
      flex-shrink: 0;
      border: ${isTv ? "5px solid rgba(255,255,255,1)" : "3px solid #ef07c1"};
      background: linear-gradient(60deg, #ff4d4d, #ffcc00, #00ff99, #0066ff, #cc00ff, #ff4d4d);
      background-size: 400% 400%;
      color: white;
      text-shadow: 0 0 6px rgba(0,0,0,0.7);
      font-family: 'Lato', sans-serif;
      font-size: ${tier.font}px;
      padding: ${tier.vipPad};
      border-radius: 20px;
      font-weight: 700;
      user-select: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      height: auto;
      overflow: hidden;
      z-index: 1;
      box-shadow:
        inset 0 0 8px rgba(0, 0, 0, 0.2),
        0 0 20px rgba(255, 255, 255, 0.6),
        0 0 40px rgba(255, 100, 100, 0.5);
      animation: rainbowSpin 9s linear infinite, pulseGlow 2s ease-in-out infinite;
      transition: transform 0.25s ease, box-shadow 0.25s ease;
    }

    .vip-button:hover {
      transform: scale(1.1);
      box-shadow:
        inset 0 0 10px rgba(0, 0, 0, 0.3),
        0 0 25px rgba(255, 255, 255, 0.8),
        0 0 60px rgba(255, 150, 150, 0.6);
    }

    .vip-button.vip-focused {
      border: 5px solid #ef07c1 !important;
      transform: scale(1.15);
      box-shadow:
        inset 0 0 10px rgba(0, 0, 0, 0.3),
        0 0 35px rgba(50, 255, 100, 0.9),
        0 0 80px rgba(50, 255, 100, 0.7);
    }

    .vip-button::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: inherit;
      filter: blur(15px);
      z-index: -1;
      animation: rainbowSpin 8s linear infinite;
    }

    @keyframes rainbowSpin {
      0% { background-position: 0% 50%; }
      100% { background-position: 400% 50%; }
    }

    @keyframes pulseGlow {
      0% {
        transform: scale(1);
        box-shadow:
          inset 0 0 8px rgba(0, 0, 0, 0.2),
          0 0 20px rgba(255, 255, 255, 0.5),
          0 0 40px rgba(255, 100, 100, 0.4);
      }
      50% {
        transform: scale(1.07);
        box-shadow:
          inset 0 0 10px rgba(0, 0, 0, 0.3),
          0 0 35px rgba(255, 255, 255, 0.8),
          0 0 70px rgba(255, 150, 150, 0.6);
      }
      100% {
        transform: scale(1);
        box-shadow:
          inset 0 0 8px rgba(0, 0, 0, 0.2),
          0 0 20px rgba(255, 255, 255, 0.5),
          0 0 40px rgba(255, 100, 100, 0.4);
      }
    }
  `}
      </style>

      <div
        style={{
          display: showBottomBar ? "flex" : "none",
        }}
      >
        <audio
          src="https://cdn.mywaitserver.live/reosrt-music-new.mp3"
          ref={resortAudioRef}
          loop={true}
        ></audio>
        <audio
          src="https://cdn.mywaitserver.live/bacgroundLIVE.mp3"
          ref={backgroundAudioRef}
          loop={true}
        ></audio>
        <audio
          src="https://cdn.mywaitserver.live/FWVO.wav"
          ref={announcementAudioRef}
        ></audio>

        {shouldShowParkBar ? (
          <ParkBottomBar device={device} onBackClick={handleParkBarBackClick} />
        ) : currentView === "VIP" ? (
          <VipBottomBar onBackClick={handleVipBackClick} />
        ) : (
          <div
            style={
              isTv
                ? {
                    position: "fixed",
                    bottom: "2.2%",
                    display: "flex",
                    justifyContent: tier.mode,
                    alignItems: "center",
                    gap: `${tier.gap}px`,
                    width: "100vw",
                    paddingRight: tier.mode === "center" ? "20px" : "0px",
                    boxSizing: "border-box",
                    zIndex: 110,
                  }
                : {
                    position: "fixed",
                    bottom: "1.5%",
                    display: "flex",
                    justifyContent: tier.mode,
                    alignItems: "center",
                    gap: `${tier.gap}px`,
                    width: "100vw",
                    paddingRight: tier.mode === "center" ? "14px" : "0px",
                    boxSizing: "border-box",
                    zIndex: 110,
                  }
            }
          >
            {isTv && (
              <button
                id={buttonIds.settings}
                ref={settingsButtonRef}
                onClick={() => setShowSettings(true)}
                onContextMenu={(e) => e.preventDefault()}
                onFocus={() => setFocusedButton("settings")}
                onBlur={() => setFocusedButton(null)}
                tabIndex={getTabIndex()}
                style={{
                  ...buttonStyle,
                  padding: "0px 4px",
                  color: showSettings ? "#3ADF3C" : "white",
                  border:
                    focusedButton === "settings" && isTv
                      ? "6px solid #3ADF3C"
                      : "6px solid transparent",
                  outline: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {showSettings ? (
                  <img
                    src="https://img.icons8.com/ios-filled/500/3ADF3C/settings.png"
                    alt=""
                    style={{ width: `${tier.iconW}px` }}
                  />
                ) : (
                  <img
                    src="https://img.icons8.com/ios-filled/500/FFFFFF/settings.png"
                    alt=""
                    style={{ width: `${tier.iconW}px` }}
                  />
                )}
              </button>
            )}
            <button
              ref={homeButtonRef}
              onClick={() => updateDeviceView("Home")}
              onContextMenu={(e) => e.preventDefault()}
              onFocus={() => setFocusedButton("home")}
              onBlur={() => setFocusedButton(null)}
              tabIndex={getTabIndex()}
              style={{
                ...buttonStyle,
                padding: "0px 4px",
                color: device.screen_view === "Home" ? "#3ADF3C" : "white",
                border:
                  focusedButton === "home" && isTv
                    ? "6px solid #3ADF3C"
                    : "6px solid transparent",
                outline: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              id={buttonIds.homeButton}
            >
              {device.screen_view === "Home" ? (
                <img
                  src="/static/home-green.png"
                  alt=""
                  style={{ width: `${tier.iconW}px` }}
                />
              ) : (
                <img
                  src="/static/home-white.png"
                  alt=""
                  style={{ width: `${tier.iconW}px` }}
                />
              )}
            </button>
            {buttonEnabled("Dash") && (
            <button
              ref={dashButtonRef}
              onClick={() => updateDeviceView(isTv ? "Dashboard" : "BroadcastDash")}
              onContextMenu={(e) => e.preventDefault()}
              onFocus={() => setFocusedButton("dash")}
              onBlur={() => setFocusedButton(null)}
              tabIndex={getTabIndex()}
              style={getButtonStyles(
                "dash",
                buttonStyle,
                device.screen_view === (isTv ? "Dashboard" : "BroadcastDash"),
              )}
            >
              DASH
            </button>
            )}
            <button
              id={buttonIds.bottomBar.resort}
              ref={resortButtonRef}
              onClick={() => setShowResortSelector(true)}
              onContextMenu={(e) => e.preventDefault()}
              onFocus={() => setFocusedButton("resort")}
              onBlur={() => setFocusedButton(null)}
              tabIndex={getTabIndex()}
              style={getButtonStyles(
                "resort",
                buttonStyle,
                device.screen_view.includes("RTV"),
              )}
            >
              RESORT
            </button>
            <button
              ref={terraceButtonRef}
              onClick={() => updateDeviceView("TRC")}
              onContextMenu={(e) => e.preventDefault()}
              onFocus={() => setFocusedButton("terrace")}
              onBlur={() => setFocusedButton(null)}
              tabIndex={getTabIndex()}
              style={getButtonStyles(
                "terrace",
                buttonStyle,
                device.screen_view === "TRC",
              )}
            >
              TERRACE
            </button>

            <button
              ref={parksButtonRef}
              onClick={() => switchToParks()}
              onContextMenu={(e) => e.preventDefault()}
              onFocus={() => setFocusedButton("parks")}
              onBlur={() => setFocusedButton(null)}
              tabIndex={getTabIndex()}
              style={getButtonStyles(
                "parks",
                buttonStyle,
                device.screen_view.includes("Ride"),
              )}
            >
              PARKS
            </button>

            <button
              ref={vacationButtonRef}
              onClick={() => updateDeviceView("VC")}
              onContextMenu={(e) => e.preventDefault()}
              onFocus={() => setFocusedButton("vacation")}
              onBlur={() => setFocusedButton(null)}
              tabIndex={getTabIndex()}
              style={getButtonStyles(
                "vacation",
                buttonStyle,
                device.screen_view.includes("VC"),
              )}
            >
              VACA
            </button>
            {buttonEnabled("Memories") && (
            <button
              ref={photosButtonRef}
              onClick={() => updateDeviceView("Photos")}
              onContextMenu={(e) => e.preventDefault()}
              onFocus={() => setFocusedButton("photos")}
              onBlur={() => setFocusedButton(null)}
              tabIndex={getTabIndex()}
              style={getButtonStyles(
                "photos",
                buttonStyle,
                device.screen_view === "Photos",
              )}
            >
              MEMORIES
            </button>
            )}

            {buttonEnabled("Band") && (
            <button
              ref={bandButtonRef}
              onClick={() =>
                updateDeviceView(hasBeaconAddOn ? "BAND" : "BAND__upgrade")
              }
              onContextMenu={(e) => e.preventDefault()}
              onFocus={() => setFocusedButton("band")}
              onBlur={() => setFocusedButton(null)}
              tabIndex={getTabIndex()}
              style={getButtonStyles(
                "band",
                buttonStyle,
                device.screen_view.includes("BAND"),
              )}
            >
              BAND+
            </button>
            )}

            {showVipButton() && (
              <button
                ref={vipButtonRef}
                onClick={handleVipClick}
                onContextMenu={(e) => e.preventDefault()}
                onFocus={() => setFocusedButton("vip")}
                onBlur={() => setFocusedButton(null)}
                tabIndex={getTabIndex()}
                className={`vip-button ${
                  focusedButton === "vip" ? "vip-focused" : ""
                }`}
              >
                VIP
              </button>
            )}
          </div>
        )}
        <img
          src="/static/frame-bottom.png"
          alt=""
          style={{
            position: "fixed",
            bottom: "0",
            left: "0",
            width: "100vw",
            zIndex: 100,
          }}
        />

        {showResortSelector && (
          <RTVSelector
            device={device}
            setShowResortSelector={setShowResortSelector}
          />
        )}
      </div>
    </>
  );
};

export default BottomBar;
